import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useShops, useShopMutations, useUsers, useAmmanAreas, useSales } from '../services/api';

const ShopsPage = () => {
  const { data: shops = [], isLoading } = useShops();
  const { data: users = [] } = useUsers();
  const { data: areas = [] } = useAmmanAreas();
  const { data: sales = [] } = useSales();
  const { createMutation, updateMutation, deleteMutation } = useShopMutations();
  
  const [open, setOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameArabic: '',
    lat: 0,
    lng: 0,
    status: '1',
    userId: '',
    saleId: '',
    ammanAreaId: '',
    minOrder: '0',
  });
  const [mapOpen, setMapOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (editingShop) {
      setFormData({
        name: editingShop.name || '',
        nameArabic: editingShop.nameArabic || '',
        lat: editingShop.lat || 0,
        lng: editingShop.lng || 0,
        status: editingShop.status || '1',
        userId: editingShop.user?.id || '',
        saleId: editingShop.sale?.id || '',
        ammanAreaId: editingShop.ammanArea?.id || '',
        minOrder: String(editingShop.minOrder ?? editingShop.min_order ?? 0),
      });
    }
  }, [editingShop]);

  const handleOpen = (shop = null) => {
    setEditingShop(shop);
    if (!shop) {
      setFormData({
        name: '',
        nameArabic: '',
        lat: 0,
        lng: 0,
        status: '1',
        userId: '',
        saleId: '',
        ammanAreaId: '',
        minOrder: '0',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingShop(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Send the fields that match the API specification
    const data = {
      name: formData.name,
      nameArabic: formData.nameArabic,
      status: formData.status,
      lat: parseFloat(formData.lat) || 0,
      lng: parseFloat(formData.lng) || 0,
      ammanAreaId: formData.ammanAreaId,
      minOrder: parseFloat(formData.minOrder) || 0,
    };

    console.log('Sending update data:', data); // Debug log

    if (editingShop) {
      updateMutation.mutate(
        { id: editingShop.id, ...data },
        {
          onSuccess: () => {
            setNotification({
              open: true,
              message: 'Shop updated successfully!',
              severity: 'success'
            });
            handleClose();
          },
          onError: (error) => {
            setNotification({
              open: true,
              message: `Failed to update shop: ${error.response?.data?.message || error.message}`,
              severity: 'error'
            });
          }
        }
      );
    } else {
      // For create, include userId as well
      const createData = {
        ...formData,
        lat: parseFloat(formData.lat) || 0,
        lng: parseFloat(formData.lng) || 0,
        saleId: formData.saleId,
        ammanAreaId: formData.ammanAreaId,
        minOrder: parseFloat(formData.minOrder) || 0,
      };
      
      createMutation.mutate(createData, {
        onSuccess: () => {
          setNotification({
            open: true,
            message: 'Shop created successfully!',
            severity: 'success'
          });
          handleClose();
        },
        onError: (error) => {
          setNotification({
            open: true,
            message: `Failed to create shop: ${error.response?.data?.message || error.message}`,
            severity: 'error'
          });
        }
      });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this shop?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          setNotification({
            open: true,
            message: 'Shop deleted successfully!',
            severity: 'success'
          });
        },
        onError: (error) => {
          setNotification({
            open: true,
            message: `Failed to delete shop: ${error.response?.data?.message || error.message}`,
            severity: 'error'
          });
        }
      });
    }
  };

  const handleMapClick = () => {
    // In a real app, this would open a map to select coordinates
    setMapOpen(true);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Shops</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add Shop
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Name (Arabic)</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Sale Code</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell align="right">Min Order</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shops.map((shop) => (
                    <TableRow key={shop.id}>
                      <TableCell>{shop.name}</TableCell>
                      <TableCell>{shop.nameArabic}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleMapClick()}>
                          <LocationIcon />
                        </IconButton>
                        {shop.lat}, {shop.lng}
                      </TableCell>
                      <TableCell>{shop.status === '1' ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell>{shop.user?.fullName || 'N/A'}</TableCell>
                      <TableCell>{shop.sale?.code || 'N/A'}</TableCell>
                      <TableCell>{shop.ammanArea?.nameArabic || 'N/A'}</TableCell>
                      <TableCell align="right">
                        {parseFloat(shop.minOrder ?? shop.min_order ?? 0).toFixed(2)} JD
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpen(shop)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(shop.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingShop ? 'Edit Shop' : 'Add New Shop'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name (Arabic)"
                  name="nameArabic"
                  value={formData.nameArabic}
                  onChange={handleChange}
                  required
                  dir="rtl"
                  margin="normal"
                />
              </Grid>
              {!editingShop && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="user-label">User *</InputLabel>
                      <Select
                        labelId="user-label"
                        id="userId"
                        name="userId"
                        value={formData.userId}
                        label="User *"
                        onChange={handleChange}
                        required
                      >
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.fullName} ({user.email})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="sale-label">Sale</InputLabel>
                      <Select
                        labelId="sale-label"
                        id="saleId"
                        name="saleId"
                        value={formData.saleId}
                        label="Sale"
                        onChange={handleChange}
                      >
                        {sales.map((sale) => (
                          <MenuItem key={sale.id} value={sale.id}>
                            {sale.code} - {sale.user?.fullName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="area-label">Amman Area *</InputLabel>
                  <Select
                    labelId="area-label"
                    id="ammanAreaId"
                    name="ammanAreaId"
                    value={formData.ammanAreaId}
                    label="Amman Area *"
                    onChange={handleChange}
                    required
                  >
                    {areas.map((area) => (
                      <MenuItem key={area.id} value={area.id}>
                        {area.name} - {area.nameArabic}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Order Amount"
                  name="minOrder"
                  type="number"
                  value={formData.minOrder}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Minimum order total required for this shop (JD)"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2} marginTop={2}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="lat"
                    type="number"
                    value={formData.lat}
                    onChange={handleChange}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="lng"
                    type="number"
                    value={formData.lng}
                    onChange={handleChange}
                    margin="normal"
                  />
                  <Button 
                    variant="outlined" 
                    startIcon={<LocationIcon />} 
                    onClick={handleMapClick}
                    sx={{ mt: 2 }}
                  >
                    Map
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === '1'}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          status: e.target.checked ? '1' : '0'
                        }))
                      }
                      name="status"
                      color="primary"
                    />
                  }
                  label={formData.status === '1' ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingShop ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Map Dialog - Placeholder for map integration */}
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Location</DialogTitle>
        <DialogContent>
          <Box height={400} display="flex" alignItems="center" justifyContent="center">
            <Typography>Map integration would go here</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              // In a real app, this would set the selected coordinates
              setFormData(prev => ({
                ...prev,
                lat: 31.9539, // Example coordinates (Amman)
                lng: 35.9106
              }));
              setMapOpen(false);
            }}
          >
            Use Current Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ShopsPage;
